import { SessionConnectionStatus } from '@/services/interfaces/Session';
import type { SessionServiceConfig, SessionInfo } from '@/services/interfaces/Session';
import type { SessionEvent } from '@/services/interfaces/SessionEvent';
import { apiManager } from '@/services/implementations/APIManager';
import { MicrophoneService } from '@/services/implementations/MicrophoneService';

/**
 * Abstract base class for session services (WebRTC and WebSocket)
 * Contains shared functionality for session management, event handling, and lifecycle management
 */
export abstract class AbstractSessionService {
  protected config: SessionServiceConfig | null = null;
  protected sessionInfo: SessionInfo | null = null;
  protected sessionConnectionStatus: SessionConnectionStatus = SessionConnectionStatus.Disconnected;
  protected microphoneService: MicrophoneService | null = null;
  
  // Store session events for console display
  protected sessionEvents: SessionEvent[] = [];
  protected sessionEventListeners: ((events: SessionEvent[]) => void)[] = [];
  
  // Status change listeners
  protected statusChangeListeners: ((status: SessionConnectionStatus, sessionInfo: SessionInfo | null) => void)[] = [];
  // 添加错误监听器
  protected errorListeners: ((error: Error) => void)[] = [];

  // Add instance tracking for memory debugging
  private static instanceCount = 0;
  protected instanceId: number;
  protected serviceName: string;

  constructor(serviceName: string) {
    AbstractSessionService.instanceCount++;
    this.instanceId = AbstractSessionService.instanceCount;
    this.serviceName = serviceName;
    console.log(`🏗️ [${this.serviceName} #${this.instanceId}] Constructor - Total instances: ${AbstractSessionService.instanceCount}`);
  }

  /**
   * Initialize the session service with configuration
   * @param microphoneService External microphone service instance (optional)
   */
  async initialize(microphoneService?: MicrophoneService | null): Promise<void> {
    // Use provided microphone service or create a new one
    if (microphoneService) {
      console.log(`[${this.serviceName} #${this.instanceId}] Using provided microphone service`, microphoneService);
      this.microphoneService = microphoneService;
    } else {
      console.log(`[${this.serviceName} #${this.instanceId}] No microphone service provided, creating one`);
      // Initialize microphone service (fallback for backward compatibility)
      const micConfig = {
        onAudioData: (_audioData: Float32Array) => {
          // Handle real-time audio data if needed
          // This could be used for audio visualization or processing
        }
      };
      this.microphoneService = new MicrophoneService(micConfig);
    }
    
    // Call service-specific initialization
    await this.initializeService();
  }

  /**
   * Abstract method for service-specific initialization
   * Must be implemented by concrete classes
   */
  protected abstract initializeService(): Promise<void>;

  /**
   * Start a session with the service
   */
  async startSession(config: SessionServiceConfig, trialId?: string): Promise<void> {
    if (!config || !this.microphoneService) {
      throw new Error('Service not initialized');
    }
    this.config = config;

    try {
      this.updateSessionConnectionStatus(SessionConnectionStatus.Connecting);

      // socket 链路，暂时不走 generateSessionKey，直接在浏览器明文秘钥先发起
      if (this.serviceName.toLowerCase().includes('websocket')) {
        console.log('选择 websocket');
        const now2 = Date.now();
        await this.microphoneService.initializeMicrophone(this.config.microphoneDeviceId);
        console.log(`[${this.serviceName} #${this.instanceId}] Microphone initialized, timestamp:`, Date.now() - now2);

        // Call service-specific session start logic
        await this.startServiceSession();
        return;
      }

      console.log('选择 webrtc');
      // Request session key from the backend using APIManager
      const now = Date.now();
      const data = await apiManager.generateSessionKey(this.config.voice, undefined, trialId);
      console.log(`[${this.serviceName} #${this.instanceId}] Backend returned session key, timestamp:`, Date.now() - now);

      if (!data.client_secret?.value) {
        throw new Error('Invalid session data received from backend');
      }

      this.sessionInfo = {
        id: data.id,
        chatSessionId: data.chat_session?.id,
        startTime: Date.now(),
        status: SessionConnectionStatus.Connecting,
        ephemeralKey: data.client_secret.value,
        webrtcUrl: data.webrtc_url,
        deployment: data.deployment
      };

      // Initialize microphone with specified device
      const now2 = Date.now();
      await this.microphoneService.initializeMicrophone(this.config.microphoneDeviceId);
      console.log(`[${this.serviceName} #${this.instanceId}] Microphone initialized, timestamp:`, Date.now() - now2);

      // Call service-specific session start logic
      await this.startServiceSession();

    } catch (error) {
      this.updateSessionConnectionStatus(SessionConnectionStatus.Error, error instanceof Error ? error : new Error(String(error)));
      console.log(`lalala-error:${error}`);
      throw error;
    }
  }

  /**
   * Abstract method for service-specific session start logic
   * Must be implemented by concrete classes
   */
  protected abstract startServiceSession(): Promise<void>;

  /**
   * Stop the current session
   */
  async stopSession(): Promise<void> {
    // Reset status
    this.updateSessionConnectionStatus(SessionConnectionStatus.Disconnected);
    console.log(`🛑 [${this.serviceName} #${this.instanceId}] stopSession() - Starting cleanup...`);
    
    // Call service-specific cleanup
    await this.stopServiceSession();

    // Stop microphone service
    if (this.microphoneService) {
      console.log(`🎤 [${this.serviceName} #${this.instanceId}] Closing microphone`);
      await this.microphoneService.closeMicrophone();
    }

    // Clear event listeners
    console.log(`🗑️ [${this.serviceName} #${this.instanceId}] Clearing event listeners - Events: ${this.sessionEventListeners.length}, Status: ${this.statusChangeListeners.length}, Session Events: ${this.sessionEvents.length}`);
    this.sessionEventListeners = [];
    this.sessionEvents = [];
    this.statusChangeListeners = [];

    console.log(`✅ [${this.serviceName} #${this.instanceId}] stopSession() - Cleanup completed`);
  }

  /**
   * Abstract method for service-specific session stop logic
   * Must be implemented by concrete classes
   */
  protected abstract stopServiceSession(): Promise<void>;

  /**
   * 订阅错误事件
   * @param callback 错误处理回调函数
   */
  subscribeToError(callback: (error: Error) => void) {
    this.errorListeners.push(callback);
  }

  /**
   * 处理并传播错误
   * @param error 错误对象
   */
  protected handleError(error: Error): void {
    console.error(`🚨 [${this.serviceName} #${this.instanceId}] Error occurred:`, error);
    
    // 更新会话状态为错误状态
    this.updateSessionConnectionStatus(SessionConnectionStatus.Error, error);
    
    // 通知所有错误监听器
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (err) {
        console.error('Error in error listener:', err);
      }
    });
  }

  /**
   * 清理资源时移除错误监听器
   */
  protected cleanupErrorListeners(): void {
    this.errorListeners = [];
  }

  /**
   * Cleanup resources when service is destroyed
   * Call this when the service instance is no longer needed
   */
  async destroy(): Promise<void> {
    console.log(`💥 [${this.serviceName} #${this.instanceId}] destroy() - Starting destruction...`);
    
    // First stop any active session
    await this.stopSession();
    
    // Call service-specific destruction
    await this.destroyService();
    
    // Final cleanup
    this.config = null;
    this.sessionInfo = null;
    this.microphoneService = null;
    
    // 清理错误监听器
    this.cleanupErrorListeners();
    
    // Update instance count
    AbstractSessionService.instanceCount--;
    console.log(`💀 [${this.serviceName} #${this.instanceId}] destroy() - Instance destroyed. Remaining instances: ${AbstractSessionService.instanceCount}`);
  }

  /**
   * Abstract method for service-specific destruction logic
   * Must be implemented by concrete classes
   */
  protected abstract destroyService(): Promise<void>;

  /**
   * Subscribe to status changes
   */
  subscribeToStatusChange(callback: (status: SessionConnectionStatus, sessionInfo: SessionInfo | null) => void) {
    this.statusChangeListeners.push(callback);
    
    // Immediately notify with current status
    callback(this.sessionConnectionStatus, this.sessionInfo);
  }

  /**
   * Update connection status and notify listeners
   */
  protected updateSessionConnectionStatus(status: SessionConnectionStatus, error?: Error): void {
    this.sessionConnectionStatus = status;
    
    if (this.sessionInfo) {
      this.sessionInfo.status = status;
      if (error) {
        this.sessionInfo.error = error;
      }
    }
    
    // Notify all status change listeners
    this.statusChangeListeners.forEach(listener => {
      try {
        listener(status, this.sessionInfo);
      } catch (err) {
        console.error('Error in status change listener:', err);
      }
    });
  }

  /**
   * Subscribe to session events for console display
   * @param callback Function to call with the events
   */
  subscribeToRtcEvents(callback: (events: SessionEvent[]) => void) {
    this.sessionEventListeners.push(callback);
    
    // Immediately notify with current events
    callback([...this.sessionEvents]);
  }
  
  /**
   * Get the current session events
   * @returns Array of session events
   */
  getRtcEvents(): SessionEvent[] {
    return [...this.sessionEvents];
  }
  
  /**
   * Clear session events
   */
  clearRtcEvents(): void {
    this.sessionEvents = [];
    this.notifySessionEventListeners();
  }
  
  /**
   * Log a session event
   * @param event Event to log
   */
  protected logSessionEvent(event: SessionEvent): void {
    // Add event ID and timestamp if not present
    if (!event.event_id) {
      event.event_id = `event_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    }
    
    if (!event.timestamp) {
      event.timestamp = new Date().toISOString();
    }
    
    // Add to events array
    this.sessionEvents.unshift(event);
    
    // Notify listeners
    this.notifySessionEventListeners();
  }
  
  /**
   * Notify all session event listeners
   */
  protected notifySessionEventListeners(): void {
    const events = [...this.sessionEvents];
    this.sessionEventListeners.forEach(listener => {
      try {
        listener(events);
      } catch (error) {
        console.error('Error in session event listener:', error);
      }
    });
  }

  /**
   * Abstract method for updating session instructions
   * Must be implemented by concrete classes
   */
  abstract updateInstructions(): Promise<void>;
}
